"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProductBlueprint = void 0;
const class_validator_1 = require("class-validator");
const Product_1 = require("./Product");
const restrictions_1 = require("./restrictions");
const pricingRules_1 = require("./pricingRules");
const BlueprintStockedProduct_1 = require("./BlueprintStockedProduct");
const TProductType_1 = require("../types/TProductType");
const react_1 = require("react");
/**
 * Product Blueprints are customisable goods for sale.
 * They are not ready for sale until built. For example, burritos.
 * The building process is governed by their available products,
 * restriction rules and pricing rules.
 */
class ProductBlueprint extends Product_1.Product {
    constructor() {
        super(...arguments);
        this.category = Product_1.defaultProductCategory;
    }
    get productsMap() {
        // lookup already exists so just use this one
        if (this._productsMap) {
            return this._productsMap;
        }
        // doesn't exist yet
        // build a lookup indexed by the ingredient id to the full ingredient model
        const productsMap = new Map();
        this.products.forEach((blueprintStockedProduct) => {
            productsMap.set(blueprintStockedProduct.productId, blueprintStockedProduct);
        });
        this._productsMap = productsMap;
        return this._productsMap;
    }
    get productsByCategory() {
        // map has already been so just return the value
        if (this._productsByCategory) {
            return this._productsByCategory;
        }
        const productsByCategoryMap = new Map();
        // go through each product to build the map
        this.products.forEach((product) => {
            const { category: { name } } = product;
            // check if the category already exists
            const productsByCategory = productsByCategoryMap.get(name);
            // create the category name as the key then add to the existing
            // products under it
            productsByCategoryMap.set(name, (productsByCategory || []).concat(product));
        });
        return productsByCategoryMap;
    }
    get categoriesMap() {
        // lookup already exists so just return it
        if (this._categoriesMap instanceof Map) {
            return this._categoriesMap;
        }
        // otherwise if it doesn't exist, create the look up indexed by the name mapped to the full category data
        const categoriesMap = new Map();
        this.categories.forEach((category) => {
            const { name: categoryName } = category;
            categoriesMap.set(categoryName, category);
        });
        this._categoriesMap = categoriesMap;
        return this._categoriesMap;
    }
    /**
     * Map of current product availability by product id
     */
    get productAvailabilityMap() {
        return this.buildProductAvailabilityMap();
    }
    get type() {
        return TProductType_1.TProductType.ProductBlueprint;
    }
    get isOutOfStock() {
        return this.products.every((product) => product.isOutOfStock);
    }
    get categories() {
        // categories have already been built so just return it
        if (Array.isArray(this._categories)) {
            return this._categories;
        }
        // otherwise go through all the categories and build it as an array
        // just get all categories within all ingredients
        const allCategories = this.products
            // just in case category is not defined
            .filter((blueprintStockedProduct) => blueprintStockedProduct.category)
            .map((blueprintStockedProduct) => blueprintStockedProduct.category);
        // remove any duplicates
        const uniqueCategories = allCategories.filter((categoryA, index) => allCategories.findIndex((categoryB) => categoryA.name === categoryB.name) === index);
        // sort the categories by their priority shown in the builder
        const sortedCategoriesByPriority = uniqueCategories.sort((categoryA, categoryB) => categoryA.priority - categoryB.priority);
        this._categories = sortedCategoriesByPriority;
        return this._categories;
    }
    get requiredCategoriesMap() {
        // lookup already exists so just return the data
        if (this._requiredCategoriesMap instanceof Map) {
            return this._requiredCategoriesMap;
        }
        // otherwise we create the lookup
        const requiredCategoriesMap = new Map();
        const { productsByCategory, restrictions } = this;
        // for each category find out which ones need to have a selection
        this.categories.forEach((category) => {
            const { name: categoryName } = category;
            const categoryProducts = productsByCategory.get(categoryName);
            // get the rule that determines what needs to be selected in the category choice
            const categoryRestrictionRule = restrictions.find((restriction) => (restriction.name === restrictions_1.TRestrictionRuleName.categoryMinMax ||
                restriction.name ===
                    restrictions_1.TRestrictionRuleName.categoryMutuallyExclusive) &&
                restriction.category === categoryName);
            // a category choice is required if the required number of selections is equal to 1 or more
            let categorySelectionRequired = false;
            if (categoryRestrictionRule !== undefined) {
                categorySelectionRequired = categoryProducts.some((blueprintStockedProduct) => categoryRestrictionRule.min -
                    blueprintStockedProduct.defaultLevel >=
                    1);
            }
            // if a category has individual choices rather than a category one then it is considered required if all ingredients have been
            // selected one or more times
            const allItemsSelectionRequired = categoryProducts.every((blueprintStockedProduct) => {
                const itemRestrictionRule = restrictions.find((restriction) => restriction.name === restrictions_1.TRestrictionRuleName.itemMinMax &&
                    restriction.productId === blueprintStockedProduct.productId);
                if (itemRestrictionRule === undefined) {
                    return false;
                }
                return (itemRestrictionRule.min - blueprintStockedProduct.defaultLevel >= 1);
            });
            // lookup indexed by category name and value boolean whether it is required
            requiredCategoriesMap.set(categoryName, categorySelectionRequired || allItemsSelectionRequired);
        });
        this._requiredCategoriesMap = requiredCategoriesMap;
        return this._requiredCategoriesMap;
    }
    get categoryMinMaxRestrictions() {
        const { categories, restrictions, _categoryMinMaxRestrictions } = this;
        // lookup already built so just return the data
        if (_categoryMinMaxRestrictions instanceof Map) {
            return _categoryMinMaxRestrictions;
        }
        // otherwise we find the restriction rule for each category if it exists
        // create a look up indexed by the category name and the category restriction model or undefined if it doesn't exist as the values
        const categoryMinMaxRestrictionsMap = new Map();
        categories.forEach((category) => {
            const { name: categoryName } = category;
            const categoryMinMaxRestriction = restrictions.find((restriction) => (restriction.name === restrictions_1.TRestrictionRuleName.categoryMinMax ||
                restriction.name ===
                    restrictions_1.TRestrictionRuleName.categoryMutuallyExclusive) &&
                restriction.category === categoryName);
            categoryMinMaxRestrictionsMap.set(categoryName, categoryMinMaxRestriction);
        });
        this._categoryMinMaxRestrictions = categoryMinMaxRestrictionsMap;
        return this._categoryMinMaxRestrictions;
    }
    get blueprintStockedProductMinMaxRestrictions() {
        const { products, restrictions, _blueprintStockedProductMinMaxRestrictions } = this;
        // lookup already built so just return the data
        if (_blueprintStockedProductMinMaxRestrictions instanceof Map) {
            return _blueprintStockedProductMinMaxRestrictions;
        }
        // otherwise we find the restriction rule for each product if it exists
        // create a lookup indexed by the product id and the item restriction model or undefined if it doesn't exist as the values
        const blueprintStockedProductMinMaxRestrictionsMap = new Map();
        products.forEach((blueprintStockedProduct) => {
            const { productId } = blueprintStockedProduct;
            const blueprintStockedProductMinMaxRestriction = restrictions.find((restriction) => restriction.name === restrictions_1.TRestrictionRuleName.itemMinMax &&
                restriction.productId === productId);
            blueprintStockedProductMinMaxRestrictionsMap.set(productId, blueprintStockedProductMinMaxRestriction);
        });
        this._blueprintStockedProductMinMaxRestrictions = blueprintStockedProductMinMaxRestrictionsMap;
        return this._blueprintStockedProductMinMaxRestrictions;
    }
    get blueprintStockedProductMinMaxQuantities() {
        const { products, categoryMinMaxRestrictions, blueprintStockedProductMinMaxRestrictions, _blueprintStockedProductMinMaxQuantities } = this;
        // lookup already built so just return the data
        if (_blueprintStockedProductMinMaxQuantities instanceof Map) {
            return _blueprintStockedProductMinMaxQuantities;
        }
        const blueprintStockedProductMinMaxQuantitiesMap = new Map();
        // otherwise we loop through each product and create the lookup
        products.forEach((blueprintStockedProduct) => {
            const { productId, category: { name } } = blueprintStockedProduct;
            // find all the min max restrictions relating to the product
            const categoryMinMaxRestriction = categoryMinMaxRestrictions.get(name);
            const productMinMaxRestriction = blueprintStockedProductMinMaxRestrictions.get(productId);
            // combine them
            const allMinMaxRestrictions = []
                .concat(categoryMinMaxRestriction || [])
                .concat(productMinMaxRestriction || []);
            // the minimum quantity allowed for the product would be the minimum of all restriction rule minimums
            // for example:
            //  1. Product A
            //    - category restriction min = 1
            //    - item restriction min = 2
            //    actual minimum = 1
            //  2. Product B
            //    - category restriction min = 2
            //    - item restriction min = 1
            //    actual minimum = 1
            //  3. Product C
            //    - category restriction min = 2
            //    actual minimum = 2
            //  3. Product D
            //    - item restriction min = 2
            //    actual minimum = 2
            const minQuantity = Math.min(...allMinMaxRestrictions.map((minMaxRestriction) => minMaxRestriction.min));
            // the maximum quantity allowed for the product would be the minimum of all restriction rule maximums
            // for example:
            //  1. Product A
            //    - category restriction max = 1
            //    - item restriction max = 2
            //    actual maximum = 1
            //  2. Product B
            //    - category restriction max = 2
            //    - item restriction max = 1
            //    actual maximum = 1
            //  3. Product C
            //    - category restriction max = 2
            //    actual maximum = 2
            //  3. Product D
            //    - item restriction max = 2
            //    actual maximum = 2
            const maxQuantity = Math.min(...allMinMaxRestrictions.map((minMaxRestriction) => minMaxRestriction.max));
            blueprintStockedProductMinMaxQuantitiesMap.set(productId, {
                min: minQuantity,
                max: maxQuantity
            });
        });
        this._blueprintStockedProductMinMaxQuantities = blueprintStockedProductMinMaxQuantitiesMap;
        return this._blueprintStockedProductMinMaxQuantities;
    }
    get categoryPricingRules() {
        const { categories, pricingRules, _categoryPricingRules } = this;
        // lookup already built so just return the data
        if (_categoryPricingRules instanceof Map) {
            return _categoryPricingRules;
        }
        // otherwise we find the pricing rule for each category if it exists
        // create a look up indexed by the category name and the category pricing rule model or undefined if it doesn't exist as the values
        const categoryPricingRulesMap = new Map();
        categories.forEach((category) => {
            const { name: categoryName } = category;
            const categoryPricingRule = pricingRules.find((pricingRule) => pricingRule.name === pricingRules_1.TPricingRuleName.complementaryByCategory &&
                pricingRule.category === categoryName);
            categoryPricingRulesMap.set(categoryName, categoryPricingRule);
        });
        this._categoryPricingRules = categoryPricingRulesMap;
        return this._categoryPricingRules;
    }
    get blueprintStockedProductPricingRules() {
        const { products, pricingRules, _blueprintStockedProductPricingRules } = this;
        // lookup already built so just return the data
        if (_blueprintStockedProductPricingRules instanceof Map) {
            return _blueprintStockedProductPricingRules;
        }
        // otherwise we find the pricing rule for each product if it exists
        // create a lookup indexed by the product id and the item pricing rule model or undefined if it doesn't exist as the values
        const blueprintStockedProductPricingRulesMap = new Map();
        products.forEach((blueprintStockedProduct) => {
            const { productId } = blueprintStockedProduct;
            const blueprintStockedProductPricingRule = pricingRules.find((pricingRule) => pricingRule.name === pricingRules_1.TPricingRuleName.complementaryByItem &&
                pricingRule.productId === productId);
            blueprintStockedProductPricingRulesMap.set(productId, blueprintStockedProductPricingRule);
        });
        this._blueprintStockedProductPricingRules = blueprintStockedProductPricingRulesMap;
        return this._blueprintStockedProductPricingRules;
    }
    get blueprintStockedProductComplementaryQuantities() {
        const { products, categoryPricingRules, blueprintStockedProductPricingRules, _blueprintStockedProductComplementaryQuantities } = this;
        // lookup already built so just return the data
        if (_blueprintStockedProductComplementaryQuantities instanceof Map) {
            return _blueprintStockedProductComplementaryQuantities;
        }
        const blueprintStockedProductComplementaryQuantitiesMap = new Map();
        // otherwise we loop through each product and create the lookup
        products.forEach((blueprintStockedProduct) => {
            const { productId, category: { name } } = blueprintStockedProduct;
            // find all the min max restrictions relating to the product
            const categoryPricingRule = categoryPricingRules.get(name);
            const productPricingRule = blueprintStockedProductPricingRules.get(productId);
            // combine them
            const allPricingRules = []
                .concat(categoryPricingRule || [])
                .concat(productPricingRule || []);
            // the complementary quantity for the product would be the minimum complementary rule quantities
            // for example:
            //  1. Product A
            //    - category complementary quantity = 1
            //    - item complementary quantity = 2
            //    actual complementary quantity = 1
            //  2. Product B
            //    - category complementary quantity = 2
            //    - item complementary quantity = 1
            //    actual complementary quantity = 1
            //  3. Product C
            //    - category complementary quantity = 2
            //    actual complementary quantity = 2
            //  3. Product D
            //    - item complementary quantity = 2
            //    actual complementary quantity = 2
            const complementaryQuantity = Math.min(...allPricingRules.map((pricingRule) => pricingRule.quantity));
            blueprintStockedProductComplementaryQuantitiesMap.set(productId, complementaryQuantity);
        });
        this._blueprintStockedProductComplementaryQuantities = blueprintStockedProductComplementaryQuantitiesMap;
        return this._blueprintStockedProductComplementaryQuantities;
    }
    /**
     * Determines whether this category has selections that are part of a required category or never considered to be always an extra
     */
    hasMainProductSelections(categoryName) {
        const { mainProductSelections } = this.getCategoryProductSelections(categoryName);
        return (Array.isArray(mainProductSelections) && mainProductSelections.length > 0);
    }
    // TODO: move to zam specific
    getValidAndInvalidTrtIds(validProductTrtIds) {
        const validTrtIds = new Map();
        const invalidTrtIds = new Map();
        Object.entries(this.trtIds).forEach(([productId, trtId]) => {
            if (trtId !== null && validProductTrtIds.has(trtId)) {
                validTrtIds.set(productId, trtId);
            }
            else {
                validTrtIds.set(productId, trtId);
            }
        });
        return { validTrtIds, invalidTrtIds };
    }
    /**
     * Lookup an ingredient model based on its id
     */
    findProductById(productId) {
        return this.productsMap.get(productId);
    }
    /**
     * Quick lookup to find the corresponding availability state this
     * blueprint's products
     */
    buildProductAvailabilityMap() {
        const { productAvailability: productAvailabilities } = this;
        const productAvailabilityMap = new Map();
        // go through each product to build the map
        productAvailabilities.forEach((productAvailability) => productAvailabilityMap.set(productAvailability.productId, productAvailability));
        return productAvailabilityMap;
    }
    /**
     * Determines whether a given category has an associated category restriction rule
     */
    hasCategoryRestrictionRule(categoryName) {
        const category = this.categoriesMap.get(categoryName);
        if (category === undefined) {
            return false;
        }
        return this.categoryMinMaxRestrictions.has(category.name) !== undefined;
    }
    /**
     * Determines whether a product is part of a disallowed when rule
     */
    productInDisallowedWhenRule(productId) {
        const { restrictions } = this;
        // is in a disallowed when rules if its in the rule's product ids or dependencies
        return restrictions.some((restriction) => restriction.name === restrictions_1.TRestrictionRuleName.disallowedWhen &&
            (restriction.productIds.includes(productId) ||
                restriction.dependencies.includes(productId)));
    }
    /**
     * Determines whether a given category has an associated category pricing rule
     */
    hasCategoryPricingRule(categoryName) {
        const category = this.categoriesMap.get(categoryName);
        if (category === undefined) {
            return false;
        }
        return this.categoryPricingRules.has(category.name) !== undefined;
    }
    /**
     * Determined whether a category has no products that cannot be selected more than once
     */
    noExtraSelectionsAllowedInCategory(categoryName) {
        // we only consider this for category choices
        const hasCategoryRestrictionRule = this.hasCategoryRestrictionRule(categoryName);
        const categoryBlueprintStockedProducts = this.productsByCategory.get(categoryName);
        if (!hasCategoryRestrictionRule) {
            return false;
        }
        return categoryBlueprintStockedProducts.every(categoryBlueprintStockedProduct => this.noExtraSelectionForProduct(categoryBlueprintStockedProduct.productId));
    }
    /**
     * Determines whether a product can only be selected no more than once
     */
    noExtraSelectionForProduct(productId) {
        const maxQuantity = this.blueprintStockedProductMinMaxQuantities.get(productId).max;
        return maxQuantity <= 1;
    }
    /**
     * Get the products in a category that are either:
     *  - part of a category choice or have a complementary choice
     *  - is always an extra
     */
    getCategoryProductSelections(categoryName) {
        const { productsByCategory, productAvailabilityMap } = this;
        const categoryProducts = productsByCategory.get(categoryName);
        const hasCategoryRestrictionRule = (0, react_1.useMemo)(() => this.hasCategoryRestrictionRule(categoryName), [categoryName]);
        const mainProducts = [];
        const alwaysExtras = [];
        categoryProducts.forEach((blueprintStockedProduct) => {
            const { productId } = blueprintStockedProduct;
            const productInDisallowedWhenRule = this.productInDisallowedWhenRule(productId);
            // if product has a category choice add it to the main selections
            if (hasCategoryRestrictionRule || productInDisallowedWhenRule) {
                mainProducts.push(blueprintStockedProduct);
                return;
            }
            const { isAlwaysExtra } = productAvailabilityMap.get(blueprintStockedProduct.productId);
            // if a product is always considered an extra added it to that set other it is part of the main selections
            isAlwaysExtra
                ? alwaysExtras.push(blueprintStockedProduct)
                : mainProducts.push(blueprintStockedProduct);
        });
        return {
            mainProductSelections: mainProducts,
            alwaysExtraSelections: alwaysExtras
        };
    }
    filterSensitiveData() {
        const clone = super.filterSensitiveData();
        // TODO: move to zam specific
        delete clone.trtIds;
        return clone;
    }
    serialize() {
        return Object.assign(Object.assign({}, super.serialize()), { products: this.products.map((product) => product.serialize()), restrictions: this.restrictions.map((restriction) => restriction.serialize()), pricingRules: this.pricingRules.map((pricingRule) => pricingRule.serialize()), category: this.category, 
            // TODO: move to zam specific
            trtIds: this.trtIds });
    }
    static unserialize(data) {
        const newProductBlueprint = new ProductBlueprint();
        newProductBlueprint.productId = data.productId;
        newProductBlueprint.retailLocationId = data.retailLocationId;
        newProductBlueprint.title = data.title;
        newProductBlueprint.description = data.description;
        newProductBlueprint.imageUrl = data.imageUrl;
        newProductBlueprint.retailPrice = data.retailPrice;
        newProductBlueprint.dietaryInfo = data.dietaryInfo;
        newProductBlueprint.productPriority = data.productPriority;
        newProductBlueprint.products = data.products.map((product) => BlueprintStockedProduct_1.BlueprintStockedProduct.unserialize(product));
        newProductBlueprint.productAvailability = newProductBlueprint.products.map((product) => ({
            canAdd: true,
            canRemove: true,
            productId: product.productId,
            complementaryCount: 0,
            extraPrice: 0,
            disabled: false,
            isNowExtra: false,
            isAlwaysExtra: false,
            isNeverExtra: false
        }));
        newProductBlueprint.restrictions = data.restrictions
            .map((restriction) => (0, restrictions_1.restrictionRuleFactory)(restriction))
            .filter(r => r !== null);
        newProductBlueprint.pricingRules = data.pricingRules
            .map((pricingRules) => (0, pricingRules_1.pricingRuleFactory)(pricingRules))
            .filter(pr => pr !== null);
        newProductBlueprint.category = data.category;
        // TODO: move to zam specific
        newProductBlueprint.trtIds = data.trtIds;
        return newProductBlueprint;
    }
}
__decorate([
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.ArrayNotEmpty)()
], ProductBlueprint.prototype, "products", void 0);
__decorate([
    (0, class_validator_1.IsArray)()
], ProductBlueprint.prototype, "productAvailability", void 0);
__decorate([
    (0, class_validator_1.IsArray)()
], ProductBlueprint.prototype, "restrictions", void 0);
__decorate([
    (0, class_validator_1.IsArray)()
], ProductBlueprint.prototype, "pricingRules", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)()
], ProductBlueprint.prototype, "category", void 0);
__decorate([
    (0, class_validator_1.IsOptional)()
], ProductBlueprint.prototype, "trtIds", void 0);
exports.ProductBlueprint = ProductBlueprint;
//# sourceMappingURL=ProductBlueprint.js.map